import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogOut, LayoutDashboard, Apple, CalendarDays, Scale, Pill, TestTube, Crown, Sparkles, X, Lock } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/NotificationBell";
import Overview from "@/components/nutri/Overview";
import FoodPreferences from "@/components/nutri/FoodPreferences";
import WeeklyDiet from "@/components/nutri/WeeklyDiet";
import WeightTracker from "@/components/nutri/WeightTracker";
import Supplements from "@/components/nutri/Supplements";
import BloodTests from "@/components/nutri/BloodTests";
import Subscription from "@/components/nutri/Subscription";
import { UpsellBanner } from "@/components/PremiumLock";
import { BottomNav } from "@/components/BottomNav";
import TodaySummary from "@/components/nutri/TodaySummary";
import DeleteAccount from "@/components/nutri/DeleteAccount";

const TABS = [
  { v: "overview", label: "Panoramica", icon: LayoutDashboard, limited: false },
  { v: "diet", label: "Dieta", icon: CalendarDays, limited: true },
  { v: "foods", label: "Alimenti", icon: Apple, limited: true },
  { v: "weight", label: "Peso", icon: Scale, limited: false },
  { v: "supplements", label: "Integratori", icon: Pill, limited: true },
  { v: "blood", label: "Analisi", icon: TestTube, limited: false },
  { v: "subscription", label: "Abbonamento", icon: Crown, limited: false },
];

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Buongiorno" : h < 18 ? "Buon pomeriggio" : "Buonasera";
};

export default function Dashboard() {
  const { user, setUser, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = TABS.map((t) => t.v);
  const paramTab = searchParams.get("tab");
  const dietAction = searchParams.get("action");
  const [tab, setTab] = useState(validTabs.includes(paramTab) ? paramTab : "overview");
  const [isPremium, setIsPremium] = useState(!!user?.is_premium);

  useEffect(() => {
    if (validTabs.includes(paramTab)) setTab(paramTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramTab]);

  const changeTab = (v) => {
    setTab(v);
    setSearchParams({}, { replace: true });
  };

  const loadSub = async () => {
    try { const { data } = await api.get("/subscription"); setIsPremium(data.is_premium); } catch {}
  };
  useEffect(() => { loadSub(); }, [tab]);

  useEffect(() => {
    refreshUser?.();
    const t = setTimeout(() => refreshUser?.(), 35000);
    return () => clearTimeout(t);
  }, [refreshUser]);

  const ackUpdate = async () => {
    try { await api.post("/diet/ack-autoupdate"); } catch {}
    setUser({ ...user, diet_autoupdate_pending: false });
  };

  const doLogout = async () => { await logout(); navigate("/"); };


  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/DietFlix-logo.webp" alt="DietFlix" className="h-8" />
            <span className="text-xs font-medium text-muted-foreground italic hidden md:inline" data-testid="brand-claim-header">la tua nutrizione “ondemand”</span>
            {isPremium && <Badge className="bg-accent/15 text-accent border-accent/30 gap-1 hidden sm:flex" variant="outline"><Crown className="h-3 w-3" /> Premium</Badge>}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {!isPremium && (
              <Button size="sm" className="hidden sm:flex btn-premium border-0 rounded-full" onClick={() => changeTab("subscription")} data-testid="header-upgrade-button">
                <Crown className="h-4 w-4 mr-2" /> Passa a Premium
              </Button>
            )}
            <span className="text-sm text-muted-foreground hidden sm:inline" data-testid="user-name">{user?.name}</span>
            <NotificationBell />
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4 mr-2" /> Esci
            </Button>
          </div>
        </div>
      </header>

      {user?.diet_autoupdate_pending && (
        <div className="bg-primary/10 border-b border-primary/20" data-testid="diet-update-banner">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-primary shrink-0" />
            <p className="text-sm flex-1">La tua dieta è stata <strong>aggiornata automaticamente</strong> in base alle nuove analisi del sangue.</p>
            <Button size="sm" onClick={() => { changeTab("diet"); ackUpdate(); }} data-testid="diet-update-view">Vedi la dieta</Button>
            <Button size="sm" variant="ghost" onClick={ackUpdate} data-testid="diet-update-dismiss"><X className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 pt-6 pb-28 sm:py-8 space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-brand text-white p-6 sm:p-8 shadow-xl pop-in" data-testid="dashboard-hero">
          <div className="hero-orb h-48 w-48 bg-amber-300 -right-12 -top-16" />
          <div className="hero-orb h-36 w-36 bg-cyan-300 right-40 -bottom-20" style={{ animationDelay: "3s" }} />
          <div className="relative flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/75 font-semibold">{greeting()}</div>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold leading-tight mt-1" data-testid="dashboard-greeting">
                {user?.name?.split(" ")[0] || "Ciao"}, pronto a nutrirti bene?
              </h1>
              <p className="text-white/85 text-sm mt-1 max-w-md">{isPremium ? "Il tuo piano Premium è attivo: tutta la settimana su misura ti aspetta." : "Stai usando il piano gratuito: prova la tua giornata su misura e scopri cosa sblocchi con Premium."}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => changeTab("diet")} className="bg-white text-[#7A2FF2] hover:bg-white/90 rounded-full font-semibold" data-testid="hero-diet-button">
                <CalendarDays className="h-4 w-4 mr-2" /> {isPremium ? "La mia settimana" : "La mia giornata"}
              </Button>
              {!isPremium && (
                <Button onClick={() => changeTab("subscription")} className="bg-white/15 hover:bg-white/25 text-white border border-white/40 rounded-full" data-testid="hero-upgrade-button">
                  <Crown className="h-4 w-4 mr-2" /> Passa a Premium
                </Button>
              )}
            </div>
          </div>
        </section>

        <TodaySummary onOpenWeight={() => changeTab("weight")} />
        <Tabs value={tab} onValueChange={changeTab} className="space-y-6 scroll-mt-20" id="dash-content">
          <div className="hidden sm:block sticky top-16 z-40 -mx-4 px-4 py-2 bg-background/80 backdrop-blur-md">
            <TabsList className="flex w-full overflow-x-auto scrollbar-none h-auto justify-start gap-1 bg-secondary/70 p-1.5 rounded-full" data-testid="dashboard-tabs">
              {TABS.map(({ v, label, icon: Ic, limited }) => (
                <TabsTrigger key={v} value={v} className="gap-2 rounded-full shrink-0 data-[state=active]:bg-[linear-gradient(90deg,#2E5BFF,#7A2FF2,#EC0B8C)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all" data-testid={`tab-${v}`}>
                  <Ic className="h-4 w-4" /> <span>{label}</span>
                  {limited && !isPremium && <Lock className="h-3 w-3 opacity-70" data-testid={`tab-lock-${v}`} />}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="overview" className="space-y-6">
            {!isPremium && (
              <UpsellBanner testid="overview-upsell" title="Sblocca la tua settimana completa"
                text="Dieta 7 giorni su 7, ricette, lista della spesa, preferiti illimitati e consigli su misura." cta="Scopri Premium" />
            )}
            <Overview />
          </TabsContent>
          <TabsContent value="foods"><FoodPreferences /></TabsContent>
          <TabsContent value="diet"><WeeklyDiet initialAction={dietAction} /></TabsContent>
          <TabsContent value="weight"><WeightTracker /></TabsContent>
          <TabsContent value="supplements"><Supplements /></TabsContent>
          <TabsContent value="blood"><BloodTests /></TabsContent>
          <TabsContent value="subscription"><Subscription /><DeleteAccount /></TabsContent>
        </Tabs>
      </main>
      <BottomNav tab={tab} onChange={changeTab} isPremium={isPremium} />
    </div>
  );
}
